import BaseObject from 'app/src/base/BaseObject';
import PaymentTypeCollection from 'app/src/data/models/PaymentTypeCollection';
import PaymentType from 'app/src/data/models/PaymentType';
import Filter from 'app/src/data/models/utils/Filter';

/**
 * @typedef {Object} PaymentTypes
 */
const PaymentTypes = {
  CASH: 'Cash',
  USER_DEFINED: 'User Defined',
  GIFT_CARD: 'Gift Card',
  SWIPE: 'Swipe',
  TERMINAL: 'Terminal',
  CREDIT_ACCOUNT: 'Credit Account',
  PENDING: 'Pending',
  DEPOSIT: 'Deposit',
  ECOM: 'Ecom',
  ADJUSTMENT: 'Adjustment',
};

const REFUNDABLE_TO_ANY_TYPE = 0;
const FETCH_LIMIT = 100;

let _allPaymentTypes = new PaymentTypeCollection();
let _nonArchivedPaymentTypes;

export default class PaymentTypeManager extends BaseObject {
  static get Types() {
    return PaymentTypes;
  }

  static get allAvailableTypes() {
    return _nonArchivedPaymentTypes;
  }

  /**
   * Initializes the PaymentTypeManager
   *
   * @returns {jQuery.promise}
   */
  static init() {
    let promise = _getAllPaymentTypes();

    promise.done(() => {
      _nonArchivedPaymentTypes = new PaymentTypeCollection(
        _allPaymentTypes.filter(paymentType => {
          let archived = paymentType.get('archived');

          return !archived;
        })
      );
    });

    return promise;
  }

  /**
   * Re-initialize the PaymentTypeManager
   *
   * Mainly use for testing purpose to avoid bleeding
   */
  static destroy() {
    _allPaymentTypes = new PaymentTypeCollection();
    _nonArchivedPaymentTypes = undefined;
  }

  /**
   * Retrieves the payment ID for the payment type specified.
   *
   * @param paymentType {PaymentTypes}    The payment type
   * @returns {number}                    The ID of the payment type
   */
  static getPaymentIdForType(paymentType) {
    return _getPaymentAttrForType(paymentType, 'paymentTypeID');
  }

  static getPaymentNameForType(paymentType) {
    return _getPaymentAttrForType(paymentType, 'name');
  }

  /**
   * Retrieves the payment type given a set of attributes to match against the known payment
   * types.
   *
   * @param {Object} attributes   The attributes to match against the known payment types
   * @returns {PaymentTypes} The type of payment
   */
  static getPaymentTypeByAttributes(attributes) {
    let type = null;
    if (_nonArchivedPaymentTypes.length) {
      let paymentType = _nonArchivedPaymentTypes.findWhere(attributes);

      if (paymentType) {
        switch (paymentType.get('type')) {
          case PaymentType.Types.CASH:
            type = PaymentTypes.CASH;
            break;

          case PaymentType.Types.GIFT_CARD:
            type = PaymentTypes.GIFT_CARD;
            break;

          case PaymentType.Types.CREDIT_CARD:
            // no way to know what type of transaction this is just from
            // the payment type since the payment would contain the
            // necessary information to determine if it's a swipe or terminal
            // payment.
            type = PaymentTypes.TERMINAL;
            break;

          case PaymentType.Types.CREDIT_ACCOUNT:
            type = PaymentTypes.CREDIT_ACCOUNT;
            break;

          case PaymentType.Types.USER_DEFINED:
            type = PaymentTypes.USER_DEFINED;
            break;

          case PaymentType.Types.ECOM:
            type = PaymentTypes.ECOM;
            break;

          case PaymentType.Types.ADJUSTMENT:
            type = PaymentTypes.ADJUSTMENT;
            break;

          default:
            break;
        }
      }
    }

    return type;
  }

  /**
   * Retrieves a Collection of PaymentTypes with the attribute of 'user defined' as the type.
   *
   * @returns {PaymentTypeCollection}                   User Defined PaymentTypes
   */
  static getUserDefinedPaymentTypes() {
    return new PaymentTypeCollection(
      _nonArchivedPaymentTypes.filter(paymentType => {
        const type = paymentType.get('type');
        const isOnlinePayment = paymentType.get('channel') === 'online';
        return type === PaymentType.Types.USER_DEFINED && !isOnlinePayment;
      })
    );
  }

  /**
   * Retrieves a collection of PaymentTypes which can be used for refunds given a collection
   * of payments applied to the sale.
   *
   * @params {PaymentList}  An array of Payments
   * @returns {PaymentTypeCollection}     Refundable PaymentTypes
   */
  static getRefundablePaymentTypes(payments) {
    let refundablePaymentTypes = new PaymentTypeCollection();
    if (payments && payments.length) {
      payments.forEach(payment => {
        // we support sale payment with and without the PaymentType relation
        // present in the model
        let paymentType =
          payment && payment.model.has('PaymentType')
            ? payment.model.getRelation('PaymentType', PaymentType)
            : PaymentTypeManager.getPaymentType(payment.model.get('paymentTypeID'));

        let refundPaymentTypesForPayment = _getApprovedPaymentTypesForRefund(paymentType);
        refundablePaymentTypes.add(refundPaymentTypesForPayment.models);
      });
    } else {
      // we do not restrict the payment types for adhoc refunds so if we have no prior payments
      // then just return all the payment types (except for ecom/adjustment)
      refundablePaymentTypes = new PaymentTypeCollection(
        _nonArchivedPaymentTypes.filter(paymentType => {
          let type = paymentType.get('type');
          return type !== 'ecom' && type !== 'adjustment';
        })
      );
    }

    return refundablePaymentTypes;
  }

  /**
   * Retrieves the payment type model which matches the ID specified.
   *
   * @param paymentTypeID {number}    The ID of the payment type to retrieve
   * @param includeArchived {Boolean} Whether or not to search through archived payment types as well
   * @returns {*|Backbone.Model}      The payment type
   */
  static getPaymentType(paymentTypeID, includeArchived = false) {
    let ret;

    if (includeArchived) {
      ret = _allPaymentTypes.findWhere({ paymentTypeID });
    } else {
      ret = _nonArchivedPaymentTypes.findWhere({ paymentTypeID });
    }

    return ret;
  }
}

/**
 * Recursively fetches all the payment types from the server.  Necessary since the server will
 * limit the maximum number of result per request to 100.
 *
 * @param {Number} offset The offset of the fetch
 * @param {jQuery.Deferred} deferred Resolved when the payment types have finished being retrieved.
 */
function _getPaymentTypes(offset, deferred) {
  let paymentTypes = new PaymentTypeCollection();
  let filter = new Filter()
    .exactly('limit', FETCH_LIMIT)
    .exactly('offset', offset)
    .exactly('archived', true);

  paymentTypes
    .fetch(filter)
    .done(() => {
      _allPaymentTypes.add(paymentTypes.models, { merge: true });

      if (offset + paymentTypes.length < paymentTypes.recordCount) {
        setTimeout(_getPaymentTypes, 0, offset + paymentTypes.length, deferred);
      } else {
        deferred.resolve();
      }
    })
    .fail(error => {
      deferred.reject(error);
    });
}

/**
 * Returns a promise the resolves once all payment types have been fetched from the server.
 * The payment type collection is stored in the private _allPaymentTypes collection.
 *
 * @return {jQuery.promise} The jQuery promise
 */
function _getAllPaymentTypes() {
  let deferred = $.Deferred();

  _getPaymentTypes(0, deferred);

  return deferred.promise();
}

/**
 * Retrieves an attribute of a known payment type.
 *
 * @param {PaymentTypes} paymentType    The type of payment
 * @param {string} attrName     The attribute
 * @returns {*}     The value of the attribute
 * @private
 */
function _getPaymentAttrForType(paymentType, attrName) {
  let attributeValue = null;

  if (_nonArchivedPaymentTypes && _nonArchivedPaymentTypes.length) {
    let type = paymentType;

    switch (paymentType) {
      case PaymentTypes.CASH:
        type = PaymentType.Types.CASH;
        break;

      case PaymentTypes.GIFT_CARD:
        type = PaymentType.Types.GIFT_CARD;
        break;

      case PaymentTypes.SWIPE:
      case PaymentTypes.TERMINAL:
        type = PaymentType.Types.CREDIT_CARD;
        break;

      case PaymentTypes.CREDIT_ACCOUNT:
        type = PaymentType.Types.CREDIT_ACCOUNT;
        break;

      case PaymentTypes.USER_DEFINED:
        type = PaymentType.Types.USER_DEFINED;
        break;

      default:
        break;
    }

    let typeOfPayment = _nonArchivedPaymentTypes.findWhere({ type });

    if (typeOfPayment) {
      attributeValue = typeOfPayment.get(attrName);
    }
  }

  return attributeValue;
}

/**
 * Retrieves the type of payments that may refund the specified payment type.
 *
 * @param {PaymentType} paymentType The payment type that may be refunded
 * @returns {PaymentTypeCollection}    A collection of the payments types that may refund the specified
 *                                     payment type
 * @private
 */
function _getApprovedPaymentTypesForRefund(paymentType) {
  let refundPaymentID = paymentType.get('refundAsPaymentTypeID');
  let refundablePaymentTypes = new PaymentTypeCollection();
  const originalPaymentType = paymentType.get('type');
  const originalPaymentIsOnlinePayment = paymentType.get('channel') === 'online';

  if (refundPaymentID === REFUNDABLE_TO_ANY_TYPE) {
    refundablePaymentTypes = new PaymentTypeCollection(
      PaymentTypeManager.allAvailableTypes.filter(paymentType => {
        const type = paymentType.get('type');
        const isOnlinePayment = paymentType.get('channel') === 'online';
        let availableForRefund = false;

        if (originalPaymentType === PaymentType.Types.ECOM) {
          availableForRefund =
            (type === PaymentType.Types.ECOM && isOnlinePayment) ||
            (type !== PaymentType.Types.ECOM && type !== PaymentType.Types.ADJUSTMENT);
        } else {
          availableForRefund =
            type !== PaymentType.Types.ECOM && type !== PaymentType.Types.ADJUSTMENT;
        }

        return availableForRefund;
      })
    );
  } else {
    let paymentType = PaymentTypeManager.getPaymentType(refundPaymentID);
    if (paymentType) {
      refundablePaymentTypes.add(paymentType);
    }
  }

  return refundablePaymentTypes;
}
